import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link, useHistory, useLocation } from "react-router-dom";
import { TbPlus, TbUsers } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import { fetchBaasCardPrograms, fetchBaasPeerCardPrograms } from "../../../../Services/baasCardProgramsApi";
import { formatBaasConsoleConflictMessage } from "../../../../Services/baasConsoleClient";
import { BaasCardsListSkeleton } from "../components/BaasSkeleton";
import { BAAS_CARTOES_ACCOUNTS_PATH, BAAS_CARTOES_PATH, BAAS_CARTOES_CREDITO_HASH } from "../constants";
import { CARTOES_CREATE_PATH } from "./BaasCardProgramCreatePage";
import {
  aggregateBaasProgramsCardsMetrics,
  aggregateBaasProgramsCreditMetrics,
} from "./baasHomeMetrics";
import { BaasCardProgramCard } from "./components/BaasCardProgramCard";
import { BaasCartoesKpiCards } from "./components/BaasCartoesKpiCards";
import { BaasCartoesQuickActions } from "./components/BaasCartoesQuickActions";
import { BaasCreditoKpiCards } from "./components/BaasCreditoKpiCards";
import {
  resolveBaasListInstanceNumericId,
  rowMatchesBaasListInstanceFilter,
  shouldFetchBaasPeerLists,
  stampBaasRowInstance,
} from "../baasInstanceListFilter";

const shouldFocusCredito = (search, hash) => {
  const section = new URLSearchParams(search).get("section");
  return section === "credito" || hash === `#${BAAS_CARTOES_CREDITO_HASH}`;
};

export const BaasCartoesPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { addToast } = useToast();
  const { selectedInstance, instances, listInstanceFilter } = useBaasInstance();
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const { can } = useBaasCapabilities();

  const [programs, setPrograms] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  const creditAllowed = can("credit.panel.view");
  const canCreateProgram = can("issuer.programs.create");

  const creditMetrics = useMemo(
    () => aggregateBaasProgramsCreditMetrics(programs),
    [programs],
  );

  const cardsMetrics = useMemo(
    () => aggregateBaasProgramsCardsMetrics(programs),
    [programs],
  );

  useEffect(() => {
    if (!shouldFocusCredito(location.search, location.hash)) return undefined;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(BAAS_CARTOES_CREDITO_HASH)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });

    if (new URLSearchParams(location.search).get("section") === "credito") {
      history.replace(`${BAAS_CARTOES_PATH}#${BAAS_CARTOES_CREDITO_HASH}`);
    }

    return () => window.cancelAnimationFrame(frame);
  }, [history, location.hash, location.search]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance) {
        setPrograms([]);
        return;
      }

      setDataLoading(true);
      try {
        const [own, peers] = await Promise.all([
          fetchBaasCardPrograms(selectedInstance),
          shouldFetchBaasPeerLists(selectedInstance, listInstanceFilter)
            ? fetchBaasPeerCardPrograms(selectedInstance, {
                instanceId: resolveBaasListInstanceNumericId(
                  listInstanceFilter,
                  selectedInstance,
                  instances,
                ),
              })
            : Promise.resolve([]),
        ]);
        if (!cancelled) {
          const byId = new Map();
          for (const row of own) {
            if (!row?.id) continue;
            byId.set(row.id, stampBaasRowInstance(row, selectedInstance));
          }
          for (const row of peers) {
            if (!row?.id) continue;
            byId.set(row.id, row);
          }
          setPrograms(
            [...byId.values()].filter((row) =>
              rowMatchesBaasListInstanceFilter(
                row,
                listInstanceFilter,
                selectedInstance,
                instances,
              ),
            ),
          );
        }
      } catch (error) {
        if (!cancelled) {
          setPrograms([]);
          addToast({
            type: "error",
            title: formatBaasConsoleConflictMessage(
              error,
              intl.get("baas.cartoes.programs.empty"),
            ),
          });
        }
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [addToast, selectedInstance, listInstanceFilter, instances]);

  if (isLoading || dataLoading) {
    return <BaasCardsListSkeleton count={3} />;
  }

  return (
    <div className="baas-processing min-w-0 space-y-5">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.cartoes")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.combinedSubtitle")}
          </p>
        </div>
        {canCreateProgram ? (
          <div className="flex shrink-0 flex-wrap gap-2">
            <Link to={BAAS_CARTOES_ACCOUNTS_PATH} className="btn-outline">
              <TbUsers className="h-4 w-4" />
              {intl.get("baas.cartoes.accounts.navLabel")}
            </Link>
            <Link to={CARTOES_CREATE_PATH} className="btn-primary">
              <TbPlus className="h-4 w-4" />
              {intl.get("baas.cartoes.create.button")}
            </Link>
          </div>
        ) : (
          <Link to={BAAS_CARTOES_ACCOUNTS_PATH} className="btn-outline shrink-0">
            <TbUsers className="h-4 w-4" />
            {intl.get("baas.cartoes.accounts.navLabel")}
          </Link>
        )}
      </header>

      <BaasCartoesQuickActions />

      {creditAllowed ? (
        <BaasCreditoKpiCards metrics={creditMetrics} />
      ) : (
        <section id={BAAS_CARTOES_CREDITO_HASH} className="min-w-0 w-full">
          <p className="dashboard-section-label mb-3">
            {intl.get("baas.home.creditSection")}
          </p>
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {intl.get("baas.home.capabilities.permissionDenied")}
            </p>
          </div>
        </section>
      )}

      <section className="min-w-0 space-y-3">
        <p className="dashboard-section-label">
          {programs.length === 0
            ? intl.get("baas.cartoes.sections.programs")
            : intl.get("baas.cartoes.programs.listSection", { count: programs.length })}
        </p>
        {programs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {intl.get("baas.cartoes.programs.empty")}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {programs.map((program) => (
              <BaasCardProgramCard
                key={program.id}
                program={program}
                onSelect={(selected) => history.push(`/baas/cartoes/${selected.id}`)}
              />
            ))}
          </div>
        )}
      </section>

      <BaasCartoesKpiCards metrics={cardsMetrics} />
    </div>
  );
};
